<script>

import {ref} from "vue";
import useSharedSelectData from "@/use/SharedSelectData.js";
import useTools from "@/use/Tools.js";
import useValidation from "@/use/Validation.js";
import axios from "axios";
import useSharedMethod from "@/use/SharedMethod";

export default {
    setup() {

        const {modesIrg} = useSharedSelectData();
        const {$refs} = useTools();
        const {mxRules} = useValidation();
        const {priceFormat} = useSharedMethod();

        const bruteDialog = ref(false);
        const bruteLoading = ref(false);

        const inverseRecord = ref({
            modeIrg: 'NORMAL',
            rssRate: 9.0,
            onlyTaxBase: 0,
            net: 0.0,
            rss: 0.0,
            postSalary: 0.0,
            taxBase: 0,
            irg: 0,
        });

        function calculateBrute() {
            if ($refs.formBrute.validate()) {
                bruteLoading.value = true;
                axios.post(`/payments/getBruteFromNet`, inverseRecord.value).then(response => {
                    inverseRecord.value = response.data;
                }).finally(() => {
                    bruteLoading.value = false;
                });
            }
        }

        const irgDialog = ref(false);
        const irgLoading = ref(false);
        const taxBase = ref(0);
        const mode = ref("NORMAL");
        const irg = ref(0.0);

        function calculateIrg() {
            if ($refs.formIrg.validate()) {
                irgLoading.value = true;
                axios.get(`/payments/getIrg`, {
                    params: {
                        taxBase: taxBase.value,
                        mode: mode.value,
                    }
                }).then(response => {
                    irg.value = response.data;
                }).finally(() => {
                    irgLoading.value = false;
                })
            }
        }

        const simulationDialog = ref(false);
        const simulationLoading = ref(false);
        const forms = ref([null, null, null, null]);
        const results = ref([0, 0, 0, 0]);

        function simulate() {
            if ($refs.formSimulation.validate()) {
                simulationLoading.value = true;
                axios.post(`/payments/simulateFormula`, forms.value).then(response => {
                    results.value = response.data;
                }).finally(() => {
                    simulationLoading.value = false;
                });
            }
        }

        return {
            mxRules,
            bruteDialog,
            irgDialog,
            bruteLoading,
            irgLoading,
            modesIrg,
            inverseRecord,
            taxBase,
            mode,
            irg,
            simulationDialog,
            simulationLoading,
            forms,
            results,
            simulate,
            calculateBrute,
            calculateIrg,
            priceFormat,
        }
    },
}
</script>


<template>
    <div>
        <v-menu offset-y>
            <template v-slot:activator="{ on, attrs }">
                <v-btn
                    color="teal"
                    depressed
                    small
                    class="white--text"
                    v-bind="attrs"
                    v-on="on"
                >
                    {{ $t('pay_slip.calculator') }}
                    <v-icon right>mdi-chevron-down</v-icon>
                </v-btn>
            </template>
            <v-list dense>
                <v-list-item @click="bruteDialog = true">
                    <v-icon left>mdi-calculator</v-icon>
                    <v-list-item-title>{{ $t('pay_slip.calculate_gross_from_net') }}</v-list-item-title>
                </v-list-item>
                <v-list-item @click="irgDialog = true">
                    <v-icon left>mdi-cash</v-icon>
                    <v-list-item-title>{{ $t('pay_slip.irg_scale') }}</v-list-item-title>
                </v-list-item>
                <v-list-item @click="simulationDialog = true">
                    <v-icon left>mdi-square-root</v-icon>
                    <v-list-item-title>{{ $t('pay_slip.simulation') }}</v-list-item-title>
                </v-list-item>
            </v-list>
        </v-menu>

        <v-dialog
            transition="dialog-bottom-transition"
            v-model="bruteDialog"
            max-width="900"
            scrollable
            persistent
        >

            <v-card>
                <w-toolbar color="teal" :title="$t('pay_slip.calculate_gross_from_net')"/>
                <v-card-text>
                    <v-container fluid>
                        <v-form ref="formBrute" lazy-validation>
                            <v-row dense>
                                <v-col cols="3">
                                    <w-label :label="$t('pay_slip.net_to_pay')" required/>
                                    <w-text-field
                                        v-model="inverseRecord.net"
                                        type="number"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="3">
                                    <w-label :label="$t('pay_slip.taxable_and_Not_subject_to_contributions')" required/>
                                    <w-text-field
                                        v-model="inverseRecord.onlyTaxBase"
                                        type="number"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="3">
                                    <w-label :label="$t('pay_slip.rate_rss')" required/>
                                    <w-text-field
                                        v-model="inverseRecord.rssRate"
                                        type="number"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="3">
                                    <w-label :label="$t('worker.mode_irg')" required/>
                                    <w-enum-select
                                        v-model="inverseRecord.modeIrg"
                                        :items="modesIrg"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                            </v-row>
                        </v-form>
                        <br/>
                        <v-simple-table dense>
                            <template v-slot:default>
                                <tbody>
                                <tr>
                                    <td>{{ $t('pay_slip.position_salary') }}</td>
                                    <td>
                                        <v-icon color="teal">mdi-plus-circle-outline</v-icon>
                                    </td>
                                    <td>
                                        <strong class="teal--text">
                                            {{ priceFormat(inverseRecord.postSalary) }}
                                        </strong>
                                    </td>
                                </tr>
                                <tr>
                                    <td>{{ $t('pay_slip.rss') }}</td>
                                    <td>
                                        <v-icon color="red">mdi-minus-circle-outline</v-icon>
                                    </td>
                                    <td>
                                        <strong class="red--text">
                                            {{ priceFormat(inverseRecord.rss) }}
                                        </strong>
                                    </td>
                                </tr>
                                <tr>
                                    <td>{{ $t('pay_slip.not_taxable') }}</td>
                                    <td>
                                        <v-icon color="teal">mdi-plus-circle-outline</v-icon>
                                    </td>
                                    <td>
                                        <strong class="teal--text">
                                            {{ priceFormat(0) }}
                                        </strong>
                                    </td>
                                </tr>
                                <tr>
                                    <td>{{ $t('pay_slip.taxable_salary') }}</td>
                                    <td></td>
                                    <td>
                                        <strong>
                                            {{ priceFormat(inverseRecord.taxBase) }}
                                        </strong>
                                    </td>
                                </tr>
                                <tr>
                                    <td>{{ $t('pay_slip.irg_withholding') }}</td>
                                    <td>
                                        <v-icon color="red">mdi-minus-circle-outline</v-icon>
                                    </td>
                                    <td>
                                        <strong class="red--text">
                                            {{ priceFormat(inverseRecord.irg) }}
                                        </strong>
                                    </td>
                                </tr>
                                <tr>
                                    <td>{{ $t('pay_slip.net_salary') }}</td>
                                    <td>
                                        <v-icon color="blue">mdi-equal</v-icon>
                                    </td>
                                    <td>
                                        <strong class="blue--text">
                                            {{ priceFormat(inverseRecord.net) }}
                                        </strong>
                                    </td>
                                </tr>
                                </tbody>
                            </template>
                        </v-simple-table>
                    </v-container>
                </v-card-text>
                <v-divider/>
                <v-card-actions>
                    <v-spacer/>
                    <w-btn-close @press="bruteDialog = false"/>
                    <w-btn
                        icon="mdi-calculator"
                        color="teal"
                        :text="$t('common.calculate')"
                        :loading="bruteLoading"
                        @press="calculateBrute"
                    />
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog
            transition="dialog-bottom-transition"
            v-model="irgDialog"
            max-width="900"
            scrollable
            persistent
        >

            <v-card>
                <w-toolbar color="teal" :title="$t('pay_slip.irg_scale')"/>

                <v-card-text>
                    <v-container fluid>
                        <v-form ref="formIrg" lazy-validation>
                            <v-row dense>
                                <v-col cols="6">
                                    <w-label :label="$t('pay_slip.taxable_salary')" required/>
                                    <w-text-field
                                        v-model="taxBase"
                                        type="number"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="6">
                                    <w-label :label="$t('worker.mode_irg')" required/>
                                    <w-enum-select
                                        v-model="mode"
                                        :items="modesIrg"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                            </v-row>
                        </v-form>
                        <br/>
                        <v-simple-table dense>
                            <template v-slot:default>
                                <tbody>

                                <tr>
                                    <td>{{ $t('pay_slip.irg_withholding') }}</td>
                                    <td>
                                        <strong class="red--text">{{ priceFormat(irg) }}</strong>
                                    </td>
                                </tr>
                                </tbody>
                            </template>
                        </v-simple-table>
                    </v-container>
                </v-card-text>
                <v-divider/>
                <v-card-actions>
                    <v-spacer/>
                    <w-btn-close @press="irgDialog = false"/>
                    <w-btn
                        icon="mdi-calculator"
                        color="teal"
                        :text="$t('common.calculate')"
                        :loading="irgLoading"
                        @press="calculateIrg"
                    />
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog
            transition="dialog-bottom-transition"
            v-model="simulationDialog"
            max-width="900"
            scrollable
            persistent
        >

            <v-card>
                <w-toolbar color="teal" :title="$t('pay_slip.simulation')"/>
                <v-card-text>
                    <v-container fluid>
                        <v-form ref="formSimulation" lazy-validation>
                            <v-row dense
                                   v-for="(v, index) in forms"
                                   :key="index"
                            >
                                <v-col cols="8">
                                    <w-label :label="`R${index + 1}`"/>
                                    <w-text-field v-model="forms[index]"/>
                                </v-col>
                                <v-col cols="4">
                                    <w-label :label="$t('common.RESULT')" />
                                    <w-text-field
                                        v-model="results[index]"
                                        readonly
                                    />
                                </v-col>
                            </v-row>
                        </v-form>
                    </v-container>
                </v-card-text>
                <v-divider/>
                <v-card-actions>
                    <v-spacer/>
                    <w-btn-close @press="simulationDialog = false"/>
                    <w-btn
                        icon="mdi-calculator"
                        color="teal"
                        :text="$t('common.calculate')"
                        :loading="simulationLoading"
                        @press="simulate"
                    />
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<style scoped>

</style>